import React, { useEffect } from 'react'

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { AxiosError, AxiosResponse } from 'axios'

import { axiosInstance } from '../../../helpers'
import {
    fetchRentRollSummary,
    downloadRentRoll,
    reparseRentRoll,
    reportRentRollIssue,
} from '../services'
import {
    PaginatedResponse,
    RentRollRow,
    Attribute,
    RentRollCellValue,
    AttributeDataType,
    TaskProgressResponse,
    RentRollSummaryResponse,
} from '../types'

// Helper functions for data_type conversion
const fromBackendDataType = (
    data_type: string | null,
): AttributeDataType | null => {
    return data_type ? (data_type.toUpperCase() as AttributeDataType) : null
}

// API response types (different from our internal types)
interface ApiAttribute {
    id: number
    name: string
    data_type: string | null
    prompt: string
    display_order: number
    should_display: boolean
    should_extract: boolean
}

interface ApiRentRollRow {
    id: number
    resident: string
    lease_start: string
    lease_end: string
    bldg_unit: string
    term_status: string
    attribute_values: Array<{
        attribute: number
        value: string
    }>
}

export interface UseRentRollOptions {
    auditId: number
    page?: number
    pageSize?: number
    search?: string
    enabled?: boolean
    progressTaskId?: string | null
    sortColumn?: string | null
    summaryEnabled?: boolean
}

export interface RentRollResponse {
    rows: RentRollRow[]
    attributes: Attribute[]
    total_count: number
}

export interface UpdateAttributeParams {
    attributeId: number
    should_display?: boolean
    should_extract?: boolean
    prompt?: string
    data_type?: string | null
    display_order?: number
}

export interface UpdateCellParams {
    rowId: number
    attributeName: string
    value: string | number | null
}

export interface UploadRentRollResponse {
    task_id: string
}

export interface AttributeMappingResponse {
    task_id: string
    lease_file_audit_id: number
    message: string
}

export interface AttributeMappingTaskStatusResponse {
    status: 'PENDING' | 'SUCCESS' | 'FAILED' | 'PROGRESS'
    result?: any
    error?: string
}

export const useRentRoll = (options: UseRentRollOptions) => {
    const {
        auditId,
        page = 1,
        pageSize = 50,
        search = '',
        enabled = true,
        progressTaskId,
        sortColumn,
        summaryEnabled = false,
    } = options
    const queryClient = useQueryClient()

    // Fetch rent roll data
    const {
        data: rentRollData,
        isLoading,
        isFetching: isFetchingRentRoll,
        error,
        refetch,
    } = useQuery<AxiosResponse<PaginatedResponse<ApiRentRollRow>>, Error>({
        queryKey: ['rentRoll', auditId, page, pageSize, search, sortColumn],
        queryFn: () => {
            return axiosInstance.get(`/lease-audit/${auditId}/rent-roll/`, {
                params: {
                    page: page,
                    page_size: pageSize,
                    search: search,
                    sort_column: sortColumn,
                },
            })
        },
        enabled: enabled && !!auditId,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })

    // Fetch attributes
    const { data: attributesData, isLoading: isLoadingAttributes } = useQuery<
        AxiosResponse<ApiAttribute[]>,
        Error
    >({
        queryKey: ['rentRollAttributes', auditId],
        queryFn: () => axiosInstance.get(`/lease-audit/${auditId}/attributes/`),
        enabled: enabled && !!auditId,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })

    // Update attribute mutation
    const updateAttributeMutation = useMutation({
        mutationFn: (params: UpdateAttributeParams) =>
            axiosInstance.patch(
                `/lease-audit/${auditId}/attributes/${params.attributeId}/`,
                params,
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['rentRollAttributes', auditId],
            })
        },
    })

    // Update cell value mutation
    const updateCellMutation = useMutation({
        mutationFn: (params: UpdateCellParams) =>
            axiosInstance.patch(
                `/lease-audit/${auditId}/rent-roll/${params.rowId}/`,
                {
                    attribute: params.attributeName,
                    value: params.value,
                },
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['rentRoll', auditId],
            })
        },
    })

    // Delete rent roll mutation
    const deleteRentRollMutation = useMutation({
        mutationFn: () =>
            axiosInstance.delete(`/lease-audit/${auditId}/rent-roll/clear/`),
        onSuccess: () => {
            queryClient.removeQueries({
                queryKey: ['rentRollSummary', auditId],
            })
            queryClient.invalidateQueries({
                queryKey: ['rentRoll', auditId],
            })
            queryClient.invalidateQueries({
                queryKey: ['rentRollAttributes', auditId],
            })
        },
    })

    const downloadRentRollMutation = useMutation({
        mutationFn: (fallbackFilename?: string) =>
            downloadRentRoll(auditId, fallbackFilename),
    })

    // Upload new rent roll mutation
    const uploadRentRollMutation = useMutation({
        mutationFn: (file: File) => {
            const formData = new FormData()
            formData.append('rent_roll_file', file)
            return axiosInstance.post<UploadRentRollResponse>(
                `/lease-audit/${auditId}/rent-roll/`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                },
            )
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['leaseAuditDetail', auditId],
            })
            queryClient.invalidateQueries({
                queryKey: ['rentRollSummary', auditId],
            })
        },
    })

    const fetchIntegratedRentRollMutation = useMutation({
        mutationFn: () =>
            axiosInstance.post<UploadRentRollResponse>(
                `/lease-audit/${auditId}/rent-roll/`,
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['leaseAuditDetail', auditId],
            })
        },
    })

    // Complete attribute selection - signals backend to advance to next step
    const completeAttributeSelectionMutation = useMutation({
        mutationFn: () =>
            axiosInstance.patch(`/lease-audit/${auditId}/`, {
                latest_ready_step: 'DATA_EXTRACTION',
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['leaseAuditDetail', auditId],
            })
        },
    })

    const {
        data: summaryData,
        isLoading: isLoadingSummary,
        isFetching: isFetchingSummary,
        error: summaryError,
        refetch: refetchSummary,
    } = useQuery<AxiosResponse<RentRollSummaryResponse>, AxiosError>({
        queryKey: ['rentRollSummary', auditId],
        queryFn: () => fetchRentRollSummary(auditId),
        enabled: enabled && !!auditId && summaryEnabled,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        retry: (failureCount, queryError) => {
            if (queryError.response?.status === 404) {
                return false
            }
            return failureCount < 1
        },
    })

    const confirmParseMutation = useMutation({
        mutationFn: () => reparseRentRoll(auditId),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['leaseAuditDetail', auditId],
            })
        },
    })

    const reportIssueMutation = useMutation({
        mutationFn: () => reportRentRollIssue(auditId),
    })

    // Poll for rent roll task progress
    const {
        data: taskProgressData,
        isLoading: isLoadingTaskProgress,
        error: taskProgressError,
    } = useQuery<AxiosResponse<TaskProgressResponse>, AxiosError>({
        queryKey: ['rentRollTaskProgress', progressTaskId],
        queryFn: () =>
            axiosInstance.get(
                `/lease-audit/${auditId}/rent-roll/task-status/${progressTaskId}/`,
            ),
        enabled: Boolean(progressTaskId),
        retry: (failureCount, queryError) => {
            if (queryError.response?.status === 404) {
                return false
            }
            return failureCount < 1
        },
        refetchInterval: (query) => {
            if (!query) return false
            const queryState = (query as any).state
            const queryError = queryState?.error as AxiosError | undefined
            if (queryError?.response?.status === 404) {
                return false
            }
            const latestData = queryState?.data as
                | AxiosResponse<TaskProgressResponse>
                | undefined
            if (
                latestData?.data?.task_status === 'SUCCESS' ||
                latestData?.data?.task_status === 'FAILED'
            ) {
                return false
            }
            return 5000
        },
    })

    const uploadTaskFailureMessage =
        taskProgressData?.data?.sub_tasks
            ?.map((subTask) => subTask.results.sub_task_error)
            .find((errorMessage) => !!errorMessage) ??
        taskProgressData?.data?.task_error ??
        null
    const uploadTaskHasFailed = taskProgressData?.data?.task_status === 'FAILED'

    const taskProgress = taskProgressData?.data
    const isDetecting =
        Boolean(progressTaskId) &&
        taskProgress?.task_status === 'RUNNING' &&
        taskProgress?.parse_config_status !== 'DETECTED'
    const isParsing =
        Boolean(progressTaskId) &&
        taskProgress?.task_status === 'RUNNING' &&
        taskProgress?.parse_config_status === 'DETECTED'

    useEffect(() => {
        if (taskProgressData?.data?.task_status === 'SUCCESS') {
            queryClient.invalidateQueries({
                queryKey: ['leaseAuditDetail', auditId],
            })
            queryClient.invalidateQueries({
                queryKey: ['rentRollAttributes', auditId],
            })
            queryClient.invalidateQueries({
                queryKey: ['rentRoll', auditId],
            })
            queryClient.invalidateQueries({
                queryKey: ['rentRollSummary', auditId],
            })
        }
    }, [taskProgressData?.data?.task_status, auditId, queryClient])

    useEffect(() => {
        if (taskProgressError?.response?.status === 404 && progressTaskId) {
            queryClient.invalidateQueries({
                queryKey: ['rentRollSummary', auditId],
            })
        }
    }, [taskProgressError, progressTaskId, auditId, queryClient])

    // Transform attributes from API format to internal format
    const apiAttributes = attributesData?.data ?? []
    const attributes: Attribute[] = apiAttributes.map((apiAttr) => ({
        id: apiAttr.id,
        name: apiAttr.name,
        should_display: apiAttr.should_display,
        should_extract: apiAttr.should_extract,
        prompt: apiAttr.prompt,
        data_type: fromBackendDataType(apiAttr.data_type),
        display_order: apiAttr.display_order,
    }))

    // Create attribute map for transforming rent roll rows
    const attributeMap = new Map(
        apiAttributes.map((attr) => [attr.id, attr.name]),
    )

    // Transform rent roll rows from API format to internal format
    const apiRows = rentRollData?.data?.results ?? []
    const rows: RentRollRow[] = apiRows.map((apiRow) => {
        const values: Record<string, RentRollCellValue> = {}

        apiRow.attribute_values.forEach((attrValue) => {
            const attributeName = attributeMap.get(attrValue.attribute)
            if (attributeName) {
                values[attributeName] = {
                    value: attrValue.value,
                }
            }
        })

        return {
            id: apiRow.id,
            resident: apiRow.resident,
            term_status: apiRow.term_status,
            values,
        }
    })

    const totalCount = rentRollData?.data?.count ?? rows.length
    const rentRollSummary = summaryData?.data
    const hasUploadedFile = Boolean(rentRollSummary)

    return {
        rows,
        attributes,
        totalCount,
        totalPages: Math.ceil(totalCount / pageSize),
        rentRollSummary,
        hasUploadedFile,

        isLoading,
        isFetchingRentRoll,
        isLoadingAttributes,
        isUpdatingAttribute: updateAttributeMutation.isLoading,
        isUpdatingCell: updateCellMutation.isLoading,
        isDeletingRentRoll: deleteRentRollMutation.isLoading,
        isDownloadingRentRoll: downloadRentRollMutation.isLoading,
        isUploadingRentRoll: uploadRentRollMutation.isLoading,
        isFetchingIntegratedRentRoll: fetchIntegratedRentRollMutation.isLoading,
        isLoadingTaskProgress,
        isProcessingUpload:
            Boolean(progressTaskId) &&
            taskProgressData?.data?.task_status !== 'SUCCESS' &&
            taskProgressData?.data?.task_status !== 'FAILED',
        isCompletingAttributeSelection:
            completeAttributeSelectionMutation.isLoading,
        isLoadingSummary,
        isFetchingSummary,
        isConfirmingParse: confirmParseMutation.isLoading,
        isReportingIssue: reportIssueMutation.isLoading,
        isDetecting,
        isParsing,

        progressTaskId,
        uploadTaskStatus: taskProgressData?.data?.task_status,
        uploadTaskError: uploadTaskFailureMessage,
        uploadTaskHasFailed,
        uploadTaskFailureMessage,
        rentRollTaskProgress: taskProgressData?.data,
        rentRollTaskProgressError: taskProgressError,

        error,
        summaryError,

        refetch,
        refetchSummary,
        updateAttribute: updateAttributeMutation.mutate,
        updateCell: updateCellMutation.mutate,
        deleteRentRoll: deleteRentRollMutation.mutate,
        downloadRentRoll: downloadRentRollMutation.mutate,
        uploadRentRoll: uploadRentRollMutation.mutate,
        fetchIntegratedRentRoll: fetchIntegratedRentRollMutation.mutate,
        completeAttributeSelection: completeAttributeSelectionMutation.mutate,
        confirmParse: confirmParseMutation.mutateAsync,
        reportIssue: async () => {
            const response = await reportIssueMutation.mutateAsync()
            return response.data
        },
    }
}
